import { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import * as yup from 'yup';
import { pick } from 'remeda';
import { connect, type ConnectedProps } from 'react-redux';
import { openModal } from '@/core/features/modal/state/actions';
import CustomTextField from '@/components/CustomTextField';
import TeamLogoInput from '@/components/TeamLogoInput';
import Form, { type FormElement } from '@/components/Form';
import CustomTimePickerInput from '@/components/CustomTimePickerInput';
import SelectField from '@/components/SelectField/SelectField';
import { clearTeamLogos, setTeamLogo } from '@/slices/logos/actions';
import { selectTeamALogo, selectTeamBLogo } from '@/slices/logos/selectors';
import { type Match as MatchType } from '@/slices/matches/types';
import { localUpdateMatch } from '@/slices/matches/actions';
import { type RootState } from '@/store';
import { useYupValidationResolver } from '@/utils';
import { ModalLayout } from '../../layouts';
import styles from './styles';

type EditMatchForm = {
  time: Dayjs | null;
  teamA: string;
  teamB: string;
  home_score: number;
  away_score: number;
  status: string;
};

const validationSchema = yup.object().shape({
  time: yup.mixed().required('Συμπληρώστε την Ώρα'),
  teamA: yup.string().required('Συμπληρώστε την Ομάδα Α'),
  teamB: yup.string().required('Συμπληρώστε την Ομάδα Β'),
  status: yup.string().oneOf(['live', 'closed']).required('Επιλέξτε status'),
});

type Props = ConnectedProps<typeof connector> & {
  closeModal: () => void;
  modalData: { match: MatchType };
};

const EditMatchModal = ({
  closeModal,
  modalData: { match },
  localUpdateMatch,
  openModal,
  clearTeamLogos,
  teamALogo,
  teamBLogo,
}: Props) => {
  const handleEditLogo = (teamType: 'A' | 'B') => {
    openModal({
      modalType: 'editLogo',
      modalData: {
        teamType,
        currentLogo: teamType === 'A' ? teamALogo : teamBLogo,
      },
    });
  };
  const formRef = useRef<FormElement>(null);
  const resolver = useYupValidationResolver(validationSchema);

  const defaultValues: EditMatchForm = {
    time: dayjs(match?.start_time).utc(false) || dayjs().utc(false),
    teamA: match?.home_team?.name || '',
    teamB: match?.away_team?.name || '',
    home_score: match?.home_score ?? 0,
    away_score: match?.away_score ?? 0,
    status: match?.status === 'live' ? 'live' : 'closed',
  };

  const handleDiscard = () => {
    clearTeamLogos();
    closeModal();
  };

  const handleAccept = () => {
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.submitForm();
      }
    }, 0);
  };

  const onSubmit = (formData: EditMatchForm) => {
    const { time, teamA, teamB, home_score, away_score, status } = pick(
      formData,
      ['time', 'teamA', 'teamB', 'home_score', 'away_score', 'status'],
    );

    clearTeamLogos();
    closeModal();

    const payload = {
      id: match.id,
      start_time: time?.utc(true).toISOString() || '',
      home_competitor_name: teamA,
      home_competitor_logo: teamALogo || undefined,
      away_competitor_name: teamB,
      away_competitor_logo: teamBLogo || undefined,
      home_score,
      away_score,
      status,
    };
    localUpdateMatch(payload);
  };

  const statusOptions = [
    { value: 'live', label: 'Live' },
    { value: 'closed', label: 'Τελικό' },
  ];

  return (
    <ModalLayout
      closeModal={closeModal}
      title="Επεξεργασία στοιχείων"
      onAccept={handleAccept}
      acceptText="Αποθήκευση"
      onCancel={handleDiscard}
      cancelText="Απόρριψη"
    >
      <Box sx={styles.alert}>
        Αν επεξεργαστείτε τα στοιχεία, θα διακοπεί η διασύνδεση με το
        Sportradar. Εάν το επιθυμείτε, μπορείτε να επαναφέρετε τον συγχρονισμό
        αργότερα.
      </Box>

      <Box>
        <Box sx={styles.editCardHeader}>
          <Typography sx={styles.editCardHeaderText} typography="body2">
            {match?.category?.name} | {match?.competition?.name}
          </Typography>
        </Box>

        <Form
          sx={styles.editCardBody}
          resolver={resolver}
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          ref={formRef}
        >
          <Box sx={styles.timeWrapper}>
            <Box sx={styles.timeBox}>
              <CustomTimePickerInput name="time" />
            </Box>

            <Box>
              <SelectField
                name="status"
                label="Κατάσταση"
                placeholder="Π.χ. Live"
                options={statusOptions}
              />
            </Box>
          </Box>

          <Box sx={styles.boxesDivider} />

          <Box sx={styles.teamsBox}>
            <Box sx={styles.teamBox}>
              <TeamLogoInput
                teamId={1}
                logoSrc={teamALogo || match?.home_team?.logo_url || ''}
                onEditClick={() => handleEditLogo('A')}
              />

              <CustomTextField
                name="teamA"
                label="Ομάδα A"
                placeholder="Επιλογή"
              />

              {match?.external_id?.startsWith('custom') && (
                <CustomTextField
                  name="home_score"
                  label="Σκορ Ομάδας A"
                  placeholder="Επιλογή"
                />
              )}
            </Box>

            <Box sx={styles.teamBox}>
              <TeamLogoInput
                teamId={2}
                logoSrc={teamBLogo || match?.away_team?.logo_url || ''}
                onEditClick={() => handleEditLogo('B')}
              />

              <CustomTextField
                name="teamB"
                label="Ομάδα B"
                placeholder="Επιλογή"
              />

              {match?.external_id?.startsWith('custom') && (
                <CustomTextField
                  name="away_score"
                  label="Σκορ Ομάδας B"
                  placeholder="Επιλογή"
                />
              )}
            </Box>
          </Box>
        </Form>
      </Box>
    </ModalLayout>
  );
};

const connector = connect(
  (state: RootState) => ({
    teamALogo: selectTeamALogo(state),
    teamBLogo: selectTeamBLogo(state),
  }),
  {
    localUpdateMatch,
    clearTeamLogos,
    openModal,
    setTeamLogo,
  },
);

export default connector(EditMatchModal);
